import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Briefcase, UserRound, MailCheck, CheckCircle2 } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountrySelect } from "@/components/CountrySelect";
import { useI18n } from "@/lib/i18n";
import i18n from "@/i18n";

function Shell({ title, sub, children }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col p-8">
        <Logo />
        <div className="flex-1 flex items-center"><div className="w-full max-w-sm mx-auto">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1><p className="text-sm text-muted-foreground mt-1 mb-6">{sub}</p>{children}
        </div></div>
      </div>
      <div className="hidden lg:flex bg-[hsl(var(--ink))] relative overflow-hidden items-end p-12">
        <div className="absolute -right-24 top-10 w-[28rem] h-[28rem] rounded-full ai-gradient opacity-40 blur-3xl" />
        <div className="absolute inset-0 grid-bg opacity-[0.07]" />
        <p className="relative text-white text-3xl font-bold max-w-md leading-tight">{t("Hire at the speed of AI.")}</p>
      </div>
    </div>
  );
}

const dest = (u) => (!(u.platform_role === "platform_admin" || (u.approval_status === "APPROVED" && u.email_verified)) ? "/pending" : u.account_type === "candidate" ? "/me" : "/app");

export function Login() {
  const { signIn } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const done = (data) => { signIn(data); nav(dest(data.user)); };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", f); if (data.mfa_required) setMfa(data); else done(data); } catch (e2) { setErr(errorDetail(e2)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { done((await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code })).data); } catch (e2) { setErr(errorDetail(e2)); } finally { setBusy(false); }
  };
  const resend = async () => { try { setMfa((await api.post("/auth/mfa/resend", { mfa_token: mfa.mfa_token })).data); setErr(""); } catch (e2) { setErr(errorDetail(e2)); } };
  if (mfa) return (
    <Shell title={t("Two-step verification")} sub={mfa.method === "totp" ? t("Enter the 6-digit code from your authenticator app.") : t("Enter the 6-digit code sent to {{email}}", { email: mfa.email_masked })}>
      <form onSubmit={verify} className="space-y-3" data-testid="mfa-form">
        <Input autoFocus inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="text-center text-lg tracking-[0.5em] font-mono" data-testid="mfa-code-input" />
        {err && <p className="text-sm text-red-600" data-testid="mfa-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy || code.length !== 6} data-testid="mfa-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("Verify")}</Button>
      </form>
      <div className="flex justify-between text-sm mt-4">
        {mfa.method === "email" ? <button onClick={resend} className="text-primary" data-testid="mfa-resend">{t("Resend code")}</button> : <span />}
        <button onClick={() => { setMfa(null); setCode(""); }} className="text-muted-foreground" data-testid="mfa-back">{t("Back")}</button>
      </div>
    </Shell>
  );
  return (
    <Shell title={t("Welcome back")} sub={t("Log in to your TOPTALENT workspace.")}>
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <Input type="email" required placeholder={t("Work email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email" />
        <PasswordInput required placeholder={t("Password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="login-password" />
        <div className="flex justify-end"><Link to={`/forgot-password${f.email ? `?email=${encodeURIComponent(f.email)}` : ""}`} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">{t("Forgot password?")}</Link></div>
        {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("Log in")}</Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">{t("No account?")} <Link to="/register" className="text-primary font-medium" data-testid="to-register">{t("Create one")}</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { setErr(errorDetail(e2)); } finally { setBusy(false); }
  };
  if (sent) return (
    <Shell title={t("Check your inbox")} sub={t("If an account exists for {{email}}, we have sent a link to reset your password. It is valid for 1 hour.", { email })}>
      <div className="flex items-center gap-3 rounded-lg border p-4 text-sm" data-testid="forgot-sent"><MailCheck className="w-5 h-5 text-primary" />{t("Don't see it? Check your spam folder or try again in a few minutes.")}</div>
      <p className="text-sm text-muted-foreground mt-6">{t("Still stuck? Write to us at")} <a href="mailto:contact@toptalent.io" className="text-primary" data-testid="forgot-sent-contact">{/* i18n-ignore */}contact@toptalent.io</a></p>
      <p className="text-sm text-muted-foreground mt-2"><Link to="/login" className="text-primary font-medium" data-testid="forgot-back-login">{t("Back to login")}</Link></p>
    </Shell>
  );
  return (
    <Shell title={t("Forgot your password?")} sub={t("Enter your account email and we will send you a link to choose a new password.")}>
      <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
        <Input type="email" required autoFocus placeholder={t("Email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
        {err && <p className="text-sm text-red-600" data-testid="forgot-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("Send reset link")}</Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">{t("Still stuck? Write to us at")} <a href="mailto:contact@toptalent.io" className="text-primary" data-testid="forgot-contact">{/* i18n-ignore */}contact@toptalent.io</a></p>
      <p className="text-sm text-muted-foreground mt-2"><Link to="/login" className="text-primary font-medium" data-testid="forgot-to-login">{t("Back to login")}</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.confirm) return setErr(t("The two passwords do not match."));
    setBusy(true);
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password: f.password }); setDone(true); } catch (e2) { setErr(errorDetail(e2)); } finally { setBusy(false); }
  };
  if (done) return (
    <Shell title={t("Password updated")} sub={t("You can now log in with your new password. Other sessions have been signed out.")}>
      <div className="flex items-center gap-3 rounded-lg border p-4 text-sm" data-testid="reset-done"><CheckCircle2 className="w-5 h-5 text-emerald-600" />{t("Your password has been changed.")}</div>
      <Button asChild className="w-full mt-4"><Link to="/login" data-testid="reset-to-login">{t("Log in")}</Link></Button>
    </Shell>
  );
  return (
    <Shell title={t("Choose a new password")} sub={t("Use at least 8 characters.")}>
      <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
        <PasswordInput required minLength={8} placeholder={t("New password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="reset-password" />
        <PasswordInput required minLength={8} placeholder={t("Confirm new password")} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testid="reset-confirm" />
        {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("Update password")}</Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6"><Link to="/forgot-password" className="text-primary font-medium" data-testid="reset-new-link">{t("Request a new link")}</Link></p>
    </Shell>
  );
}

export function Register() {
  const { signIn } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get("type") === "candidate" ? "candidate" : "recruiter");
  const [f, setF] = useState({ full_name: "", email: "", password: "", organization_name: "", country: "", website: "", job_title: "", professional_field: "", profile_url: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", { ...Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v || null])), full_name: f.full_name, email: f.email, password: f.password, account_type: type, locale: i18n.language });
      signIn(data); nav(dest(data.user));
    } catch (e2) { setErr(errorDetail(e2)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title={type === "recruiter" ? t("Start your 7-day free trial") : t("Create your free profile")} sub={type === "recruiter" ? t("No payment card required.") : t("Free forever. You control visibility and contact.")}>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {[["recruiter", t("I'm hiring"), Briefcase], ["candidate", t("I'm a candidate"), UserRound]].map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => setType(k)} data-testid={`register-type-${k}`} className={`rounded-lg border p-3 text-sm font-medium flex items-center gap-2 transition-colors ${type === k ? "border-primary bg-accent text-accent-foreground" : "hover:bg-muted"}`}><Icon className="w-4 h-4" />{l}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <Input required placeholder={t("Full name")} value={f.full_name} onChange={set("full_name")} data-testid="register-name" />
        <Input type="email" required placeholder={t("Email")} value={f.email} onChange={set("email")} data-testid="register-email" />
        <PasswordInput required minLength={8} placeholder={t("Password (8+ characters)")} value={f.password} onChange={set("password")} testid="register-password" />
        {type === "recruiter" && <Input placeholder={t("Organization name")} value={f.organization_name} onChange={set("organization_name")} data-testid="register-org" />}
        {type === "recruiter" && <Input placeholder={t("Company website")} value={f.website} onChange={set("website")} data-testid="register-website" />}
        <Input placeholder={type === "recruiter" ? t("Professional role (e.g. Talent Acquisition Manager)") : t("Current job title")} value={f.job_title} onChange={set("job_title")} data-testid="register-job-title" />
        {type === "candidate" && <Input placeholder={t("Professional field")} value={f.professional_field} onChange={set("professional_field")} data-testid="register-field" />}
        {type === "candidate" && <Input placeholder={t("Professional profile URL (optional)")} value={f.profile_url} onChange={set("profile_url")} data-testid="register-profile-url" />}
        <CountrySelect value={f.country} onChange={(country) => setF({ ...f, country })} placeholder={t("Country")} testid="register-country" />
        {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("Create account")}</Button>
        <p className="text-[11px] text-muted-foreground">{t("By creating an account you accept the")} <Link to="/terms" className="underline">{t("Terms")}</Link> {t("and the")} <Link to="/privacy" className="underline">{t("Privacy Policy")}</Link>.</p>
      </form>
      <p className="text-sm text-muted-foreground mt-6">{t("Already registered?")} <Link to="/login" className="text-primary font-medium" data-testid="to-login">{t("Log in")}</Link></p>
    </Shell>
  );
}
